/** @jsxImportSource @emotion/react */
import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode } from 'react'
import { 
    QuestionnaireFormData, 
    initialFormData, 
    FAQItem,
    FormContextType
} from '../../common/context/SharedFormTypes'
import { formReducer } from '../../common/context/useFormReducer'
import { StorageContext, ImageKey } from '../../../../services/indexedDBService'

// Re-export types for backward compatibility
export type { FAQItem, QuestionnaireFormData }
export { initialFormData }

// Create context
export const QuestionnaireFormContext = createContext<FormContextType | undefined>(undefined)

// Local storage key
const STORAGE_KEY = 'questionnaire-form-data'

// Function to load initial data from localStorage
const loadInitialData = (): QuestionnaireFormData => {
    try {
        const savedData = localStorage.getItem(STORAGE_KEY)
        if (savedData) {
            const parsedData = JSON.parse(savedData)
            // Merge with initialFormData to ensure all required fields exist
            return {
                ...initialFormData,
                ...parsedData
            }
        }
    } catch (error) {
        console.error('Error loading form data from localStorage:', error)
    }
    return initialFormData
}

// Context provider
interface QuestionnaireFormProviderProps {
    children: ReactNode
}

export const QuestionnaireFormProvider = ({ children }: QuestionnaireFormProviderProps) => {
    const [formData, dispatch] = useReducer(formReducer, loadInitialData())

    // Save data to localStorage whenever formData changes
    useEffect(() => {
        try {
            const dataString = JSON.stringify(formData)
            const dataSizeInMB = new Blob([dataString]).size / (1024 * 1024)
            console.log(`Saving questionnaire data to localStorage (${dataSizeInMB.toFixed(2)} MB)`)
            localStorage.setItem(STORAGE_KEY, dataString)
            console.log('Successfully saved to localStorage')
        } catch (error) {
            console.error('Error saving form data to localStorage:', error)
            if (error instanceof DOMException && error.name === 'QuotaExceededError') {
                console.error('localStorage quota exceeded! Data size too large.')
                alert('Warning: Unable to save questionnaire data - storage limit exceeded. Please use smaller images.')
            }
        }
    }, [formData])

    // Action handlers
    const updateBasicInfo = (data: Partial<QuestionnaireFormData['basicInfo']>) => {
        dispatch({ type: 'UPDATE_BASIC_INFO', payload: data })
    }

    const updateMainImages = (data: Partial<QuestionnaireFormData['mainImages']>) => {
        dispatch({ type: 'UPDATE_MAIN_IMAGES', payload: data })
    }

    const updateCeremonyLocation = (data: Partial<QuestionnaireFormData['ceremonyLocation']>) => {
        dispatch({ type: 'UPDATE_CEREMONY_LOCATION', payload: data })
    }

    const updateWeddingLocation = (data: Partial<QuestionnaireFormData['weddingLocation']>) => {
        dispatch({ type: 'UPDATE_WEDDING_LOCATION', payload: data })
    }

    const updateGuestsFAQ = (data: Partial<QuestionnaireFormData['guestsFAQ']>) => {
        dispatch({ type: 'UPDATE_GUESTS_FAQ', payload: data })
    }

    const addFAQItem = (faq: FAQItem) => {
        dispatch({ type: 'ADD_FAQ_ITEM', payload: faq })
    }

    const updateFAQAnswer = (id: string, answer: string) => {
        dispatch({ type: 'UPDATE_FAQ_ANSWER', payload: { id, answer } })
    }

    const removeFAQItem = (id: string) => {
        dispatch({ type: 'REMOVE_FAQ_ITEM', payload: id })
    }

    const updateWitnesses = (data: Partial<QuestionnaireFormData['witnesses']>) => {
        dispatch({ type: 'UPDATE_WITNESSES', payload: data })
    }

    const updateContact = (data: Partial<QuestionnaireFormData['contact']>) => {
        dispatch({ type: 'UPDATE_CONTACT', payload: data })
    }

    const updateDomain = (data: Partial<QuestionnaireFormData['domain']>) => {
        dispatch({ type: 'UPDATE_DOMAIN', payload: data })
    }

    const updateMenu = (data: Partial<QuestionnaireFormData['menu']>) => {
        dispatch({ type: 'UPDATE_MENU', payload: data })
    }

    const updateSchedule = (data: Partial<QuestionnaireFormData['schedule']>) => {
        dispatch({ type: 'UPDATE_SCHEDULE', payload: data })
    }

    const updateColors = (data: Partial<QuestionnaireFormData['colors']>) => {
        dispatch({ type: 'UPDATE_COLORS', payload: data })
    }

    const updateTables = (data: Partial<QuestionnaireFormData['tables']>) => {
        dispatch({ type: 'UPDATE_TABLES', payload: data })
    }

    const updateWeddingDetails = (data: Partial<QuestionnaireFormData['weddingDetails']>) => {
        dispatch({ type: 'UPDATE_WEDDING_DETAILS', payload: data })
    }

    const updateCurrentStep = (step: number) => {
        dispatch({ type: 'UPDATE_CURRENT_STEP', payload: step })
    }

    const loadFormData = useCallback((data: QuestionnaireFormData) => {
        dispatch({ type: 'LOAD_FROM_STORAGE', payload: data })
    }, [])

    const resetForm = useCallback(() => {
        dispatch({ type: 'RESET_FORM' })
    }, [])

    const markImageChanged = useCallback((_key: ImageKey) => {
        // No-op for questionnaire — all images are always pushed on first submission
    }, [])

    const contextValue: FormContextType = {
        formData,
        storageContext: 'questionnaire' as StorageContext,
        updateBasicInfo,
        updateMainImages,
        updateCeremonyLocation,
        updateWeddingLocation,
        updateGuestsFAQ,
        addFAQItem,
        updateFAQAnswer,
        removeFAQItem,
        updateWitnesses,
        updateContact,
        updateDomain,
        updateMenu,
        updateSchedule,
        updateColors,
        updateTables,
        updateWeddingDetails,
        updateCurrentStep,
        loadFormData,
        resetForm,
        markImageChanged
    }

    return (
        <QuestionnaireFormContext.Provider value={contextValue}>
            {children}
        </QuestionnaireFormContext.Provider>
    )
}

// Custom hook to use the context
export const useQuestionnaireForm = () => {
    const context = useContext(QuestionnaireFormContext)
    if (context === undefined) {
        throw new Error('useQuestionnaireForm must be used within a QuestionnaireFormProvider')
    }
    return context
}